import { useEffect, useState } from "react";
import { appStoreUrlFor, playStoreUrlFor, type Lang } from "./seo";

export type Platform = "ios" | "android" | "other";

/** Best-effort platform sniff. Runs only in the browser; SSR returns "other". */
export function detectPlatform(): Platform {
  if (typeof navigator === "undefined") return "other";
  const ua = navigator.userAgent || "";
  if (/android/i.test(ua)) return "android";
  if (/iPhone|iPad|iPod/i.test(ua)) return "ios";
  // iPadOS 13+ reports itself as a Mac but has touch points.
  if (/Macintosh/i.test(ua) && typeof navigator.maxTouchPoints === "number" && navigator.maxTouchPoints > 1) {
    return "ios";
  }
  return "other";
}

/**
 * Store links for the current language plus the "primary" one for the visitor's
 * device: Google Play on Android, the App Store everywhere else. The platform is
 * resolved after mount so prerendered HTML stays deterministic (App Store first).
 */
export function useStoreLinks(lang: Lang) {
  const [platform, setPlatform] = useState<Platform>("other");
  useEffect(() => {
    setPlatform(detectPlatform());
  }, []);

  const appStoreUrl = appStoreUrlFor(lang);
  const playStoreUrl = playStoreUrlFor(lang);
  const primaryStore = platform === "android" ? ("google_play" as const) : ("app_store" as const);

  return {
    platform,
    appStoreUrl,
    playStoreUrl,
    primaryStore,
    primaryUrl: primaryStore === "google_play" ? playStoreUrl : appStoreUrl,
  };
}

/**
 * Official store badges, served locally and localized. Both Google Play PNGs
 * are cropped to their visible artwork (no transparent padding), so every badge
 * can be rendered at the same CSS height with `w-auto` and they line up exactly.
 * width/height = intrinsic size, so the browser reserves the right aspect ratio.
 */
export function appStoreBadge(lang: Lang) {
  return lang === "fr"
    ? { src: "/badges/app-store-fr.svg", width: 127, height: 40 }
    : { src: "/badges/app-store-en.svg", width: 120, height: 40 };
}

export function playBadge(lang: Lang) {
  return lang === "fr"
    ? { src: "/badges/google-play-fr-badge.png", width: 646, height: 192 }
    : { src: "/badges/google-play-en-badge.png", width: 564, height: 168 };
}

export function appStoreAlt(lang: Lang) {
  return lang === "fr" ? "Télécharger dans l'App Store" : "Download on the App Store";
}

export function playStoreAlt(lang: Lang) {
  return lang === "fr" ? "Disponible sur Google Play" : "Get it on Google Play";
}
